/**
 * Stripe Embedded Checkout Component.
 * Provides a clean, embedded checkout experience using Stripe Checkout.
 */

import React, { useState } from 'react';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { subscriptionService } from '@/api/services/subscription-service';
import { Loader2, XCircle } from 'lucide-react';
import { subscriptionConfig } from '@/config/subscription-config';
import { PlanSelection } from './PlanSelection';
import { SUBSCRIPTION_PLANS, getPlanById } from '@/config/plan-config';

interface StripeEmbeddedCheckoutProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
  // New props for anonymous checkout
  isAnonymous?: boolean;
  userEmail?: string;
  // Plan selection props
  selectedPlanId?: string;
  showPlanSelection?: boolean;
}

// Inner component that uses Stripe hooks
const StripeCheckoutForm: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg">
        <EmbeddedCheckout />
      </div>
    </div>
  );
};

// Main component that handles checkout session creation
export const StripeEmbeddedCheckout: React.FC<StripeEmbeddedCheckoutProps> = ({
  onError,
  className = '',
  isAnonymous = false,
  userEmail,
  selectedPlanId: propSelectedPlanId,
  showPlanSelection = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [internalSelectedPlanId, setInternalSelectedPlanId] = useState<string>(
    SUBSCRIPTION_PLANS[0].id
  );
  const [internalShowPlanSelection, setInternalShowPlanSelection] = useState(showPlanSelection);

  // Use prop selectedPlanId if provided, otherwise use internal state
  const selectedPlanId = propSelectedPlanId || internalSelectedPlanId;
  const shouldShowPlanSelection = showPlanSelection && internalShowPlanSelection;

  // Create checkout session when plan is selected
  const createCheckoutSession = async (planId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const plan = getPlanById(planId);
      if (!plan) {
        throw new Error('Invalid plan selected');
      }

      let response;

      if (isAnonymous) {
        // For anonymous checkout, we need userEmail
        if (!userEmail) {
          throw new Error('User email is required for anonymous checkout');
        }
        response = await subscriptionService.createAnonymousEmbeddedCheckout(
          userEmail,
          plan.priceId
        );
      } else {
        // For authenticated checkout
        response = await subscriptionService.createEmbeddedCheckout(plan.priceId);
      }

      setClientSecret(response.client_secret);
      setInternalShowPlanSelection(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize checkout';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanSelect = (planId: string) => {
    setInternalSelectedPlanId(planId);
    createCheckoutSession(planId);
  };

  // Auto-create checkout session if selectedPlanId is provided and we shouldn't show plan selection
  React.useEffect(() => {
    if (propSelectedPlanId && !shouldShowPlanSelection && !clientSecret && !isLoading) {
      createCheckoutSession(propSelectedPlanId);
    }
  }, [propSelectedPlanId, shouldShowPlanSelection, clientSecret, isLoading]);

  // Show plan selection first
  if (shouldShowPlanSelection) {
    return (
      <div className={`bg-gray-900 rounded-lg p-6 ${className}`}>
        <PlanSelection selectedPlanId={selectedPlanId} onPlanSelect={handlePlanSelect} />
        {isLoading && (
          <div className="mt-6 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-violet-500 mr-2" />
            <span className="text-gray-300">Setting up checkout...</span>
          </div>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Initializing Checkout</h3>
            <p className="text-sm text-gray-600">Setting up secure checkout form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <XCircle className="h-8 w-8 text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Checkout Setup Failed</h3>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Checkout Form</h3>
            <p className="text-sm text-gray-600">Please wait while we prepare your checkout...</p>
          </div>
        </div>
      </div>
    );
  }

  // Initialize Stripe
  const stripePromise = loadStripe(subscriptionConfig.PUBLISHABLE_KEY);

  return (
    <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
      <StripeCheckoutForm />
    </EmbeddedCheckoutProvider>
  );
};

export default StripeEmbeddedCheckout;
